import { Fragment, useState } from 'react'
import { admin } from '../api/client'
import type {
  AdminParameter, AdminStation, AuditEntry, ParameterWrite, StationSettings,
} from '../api/types'
import { ParameterForm } from '../components/ParameterForm'
import { type AsyncState, useAsync } from '../lib/useAsync'
import { useMutation } from '../lib/useMutation'
import { Warnings } from '../components/Warnings'
import { AddStation } from '../components/AddStation'
import { StationForm } from '../components/StationForm'
import { UnmappedQueue } from '../components/UnmappedQueue'
import { RunsLog } from '../components/RunsLog'
import { GatewayRounds } from '../components/GatewayRounds'
import { RevisedReadings } from '../components/RevisedReadings'
import { UserAdmin } from '../components/UserAdmin'
import { AdminSection, AdminSections, ListSearch, useShowMore } from '../components/AdminSection'
import { useAuth } from '../lib/auth'
import { formatCount } from '../lib/format'
import { formatDateTime } from '../lib/time'
import { filterStations } from '../lib/stationSearch'
import { auditText, filterAudit, filterParameters, filterUsers } from '../lib/adminSearch'
import { VENDOR_LABEL } from '../api/types'

type Editing = { mode: 'none' } | { mode: 'create' }
  | { mode: 'edit'; code: string }

/** How many audit entries the page holds to search through. */
const AUDIT_LIMIT = 500

const plural = (n: number, one: string, many: string) =>
  `${formatCount(n)} ${n === 1 ? one : many}`

export function Admin() {
  const stations = useAsync(() => admin.stations(), [])
  const unmapped = useAsync(() => admin.unmapped(), [])
  // The admin listing rather than the public one: it carries the usage counts
  // that say whether a parameter is safe to change.
  const parameters = useAsync(() => admin.parameters(), [])
  const audit = useAsync(() => admin.audit(AUDIT_LIMIT), [])
  const users = useAsync(() => admin.users(), [])
  const { user: me } = useAuth()

  const mutation = useMutation()
  const [editing, setEditing] = useState<Editing>({ mode: 'none' })
  const [definingParameter, setDefiningParameter] = useState(false)
  const [editingParameter, setEditingParameter] = useState<string>()
  const [userQuery, setUserQuery] = useState('')

  // Fetched rather than taken from the list row: the list is station_health,
  // which does not carry the retry settings or the coordinates.
  const settings = useAsync(
    () => (editing.mode === 'edit'
      ? admin.settings(editing.code)
      : Promise.resolve(undefined)),
    [editing.mode === 'edit' ? editing.code : ''],
  )

  function reloadAll() {
    stations.reload()
    unmapped.reload()
    users.reload()
    audit.reload()
  }

  async function run(call: () => Promise<{ warnings: string[] }>) {
    // A 401 mid-mutation is handled by the signed-out listener in lib/auth,
    // which redirects to the front door; useMutation still reports it as the
    // error it was.
    const ok = await mutation.run(call)
    if (ok) {
      setEditing({ mode: 'none' })
      reloadAll()
    }
  }

  const pending = users.data?.filter((u) => u.role === 'pending').length ?? 0
  const openUnmapped = unmapped.data?.filter((u) => u.resolved_at === null).length ?? 0
  const shownUsers = filterUsers(users.data ?? [], userQuery)

  return (
    <div className="page admin">
      <header className="page-head admin-head">
        <div>
          <h1 className="sr-only">Admin</h1>
          <p className="muted">
            Every change here is written to the audit log. Some of them reach
            backwards into data already collected. Read what each one says
            it did.
            Push logins, failed pushes and the platform's health are on
            the Intake page.
          </p>
        </div>
      </header>

      {import.meta.env.VITE_DEMO_MODE === 'true' && <p className="notice" role="status">Demo administration: configuration changes, account roles and collection jobs require the connected service.</p>}

      <Warnings warnings={mutation.warnings} error={mutation.error}
                onDismiss={mutation.clear} />

      <AdminSections>
        <AdminSection id="users" title="Users"
          count={users.data ? plural(users.data.length, 'user', 'users') : undefined}
          attention={pending ? `${pending} waiting for approval` : undefined}
          note="Anyone in the Hydrodiagnostics tenant can sign in; nobody sees data until given a role here.">
          {users.loading && <p className="muted">Loading…</p>}
          {users.error && <p className="error">{users.error}</p>}
          {users.data && users.data.length > 0 && (
            <ListSearch value={userQuery} onChange={setUserQuery}
                        placeholder="Search by name, address or role"
                        shown={shownUsers.length} total={users.data.length}
                        noun={['user', 'users']} />
          )}
          {users.data && (userQuery && !shownUsers.length
            ? <p className="muted">No user matches “{userQuery.trim()}”.</p>
            : <UserAdmin users={shownUsers} me={me?.id} busy={mutation.busy}
                         onSetRole={(id, role) => run(() => admin.setUserRole(id, role))} />
          )}
        </AdminSection>

        <AdminSection id="stations" title="Stations"
          count={stations.data ? plural(stations.data.length, 'station', 'stations') : undefined}>
          <StationsTable stations={stations} editing={editing} setEditing={setEditing}
                         settings={settings} busy={mutation.busy} run={run}
                         reloadAll={reloadAll} />
        </AdminSection>

        <AdminSection id="runs" title="Collection runs"
          note="Daily collection, the weekly sweep, the monthly archive audit and backfills: what the worker did, and whether it held everything.">
          <RunsLog stations={stations.data} />
        </AdminSection>

        <AdminSection id="gateway-rounds" title="Gateway rounds"
          note="When each gateway's status is read: at minutes learned from when its nodes report, or set here. Every read goes over the gateway's SIM.">
          <GatewayRounds />
        </AdminSection>

        <AdminSection id="revisions" title="Revised readings"
          note="Readings a vendor sent that disagree with what is stored: the value a later vendor value replaced, a blank refused in favour of a real reading, and the second of two readings sent for the same time.">
          <RevisedReadings stations={stations.data} />
        </AdminSection>

        <AdminSection id="unmapped" title="Unmapped channels"
          // A count, not a call to act: Shaori's Kanal7 and Kanal8 sit here
          // unresolved on purpose (HANDBOOK.md, Known station issues), and a
          // pill would nag about them on every visit.
          count={unmapped.data ? (openUnmapped ? `${openUnmapped} unresolved` : 'none') : undefined}
          note="Channels reporting data with nowhere to store it.">
          {unmapped.loading && <p className="muted">Loading…</p>}
          {unmapped.data && parameters.data && (
            <UnmappedQueue queue={unmapped.data} parameters={parameters.data}
                           busy={mutation.busy}
                           onDefineParameter={() => setDefiningParameter(true)}
                           onResolve={(id, body) =>
                             run(() => admin.resolveUnmapped(id, body))} />
          )}
        </AdminSection>

        <AdminSection id="parameters" title="Parameters" openWhen={definingParameter}
          count={parameters.data ? plural(parameters.data.length, 'parameter', 'parameters') : undefined}
          note="What the network can measure. A channel can only be mapped to a parameter that already exists, so a station reporting something new needs one defined here first.">
          <ParametersTable parameters={parameters.data} loading={parameters.loading}
            error={parameters.error} busy={mutation.busy}
            defining={definingParameter} setDefining={setDefiningParameter}
            editing={editingParameter} setEditing={setEditingParameter}
            onCreate={(body) => run(() => admin.createParameter(body)).then(() => parameters.reload())}
            onUpdate={(code, body) => run(() => admin.updateParameter(code, body))
              .then(() => parameters.reload())} />
        </AdminSection>

        <AdminSection id="audit" title="Audit log"
          count={audit.data ? (audit.data.length >= AUDIT_LIMIT
            ? `the last ${plural(AUDIT_LIMIT, 'change', 'changes')}`
            : plural(audit.data.length, 'change', 'changes')) : undefined}
          note="Every change made here or by the gateways' renames, most recent first.">
          <AuditTable entries={audit.data} />
        </AdminSection>
      </AdminSections>
    </div>
  )
}

function StationsTable({ stations, editing, setEditing, settings, busy, run, reloadAll }: {
  stations: AsyncState<AdminStation[]>
  editing: Editing
  setEditing: (e: Editing) => void
  settings: AsyncState<StationSettings | undefined>
  busy: boolean
  run: (call: () => Promise<{ warnings: string[] }>) => Promise<void>
  reloadAll: () => void
}) {
  const [query, setQuery] = useState('')
  const all = stations.data ?? []
  const shown = filterStations(all, query)
  return (
    <>
      <div className="section-actions">
        {all.length > 0 && (
          <ListSearch value={query} onChange={setQuery}
                      placeholder="Search by name, code, id or vendor"
                      shown={shown.length} total={all.length} noun={['station', 'stations']} />
        )}
        {editing.mode === 'none' && (
          <button className="button" onClick={() => setEditing({ mode: 'create' })}>
            Add a station
          </button>
        )}
      </div>

      {editing.mode === 'create' && (
        <AddStation onCancel={() => setEditing({ mode: 'none' })}
                    onCreated={() => {
                      setEditing({ mode: 'none' })
                      reloadAll()
                    }} />
      )}

      {stations.loading && <p className="muted">Loading…</p>}
      {stations.error && <p className="error">{stations.error}</p>}
      {query && !shown.length && <p className="muted">No station matches “{query.trim()}”.</p>}

      {shown.length > 0 && (
        <table className="grid">
          <thead>
            <tr>
              <th>Station</th><th>Vendor id</th><th>Interval</th>
              <th>Collects at</th><th>Behind</th><th>Channels</th><th></th>
            </tr>
          </thead>
          <tbody>
            {shown.map((station) => (
              <Fragment key={station.code}>
                <tr className={station.active ? '' : 'inactive'}>
                  <td>
                    <strong>{station.name}</strong><br />
                    <code className="muted">{station.code}</code>
                    {!station.active && <span className="pill neutral">
                      decommissioned
                    </span>}
                  </td>
                  <td>
                    <code>{station.external_id}</code><br />
                    <span className="muted">{VENDOR_LABEL[station.vendor]}</span>
                  </td>
                  <td>{station.poll_interval_s / 60} min</td>
                  <td>
                    {station.schedule_time.slice(0, 5)}
                    {!station.schedule_enabled &&
                      <span className="pill warn">off</span>}
                  </td>
                  <td>{station.days_behind ?? '—'}</td>
                  <td>
                    {station.mapped_channels}
                    {station.unmapped_channels > 0 && (
                      <span className="pill warn">
                        +{station.unmapped_channels}
                      </span>
                    )}
                  </td>
                  <td>
                    {/* The flex row goes on a wrapper, never on the `td`
                        itself: a table cell told to be a flex container stops
                        being a table cell, so it shrinks to its own content
                        instead of the row's height and draws its bottom
                        border short of the row's. */}
                    <div className="row-actions">
                      <button className="link" onClick={() =>
                        setEditing({ mode: 'edit', code: station.code })}>
                        edit
                      </button>
                      {station.active ? (
                        <button className="link danger" disabled={busy}
                                onClick={() => run(() =>
                                  admin.deactivateStation(station.code))}>
                          decommission
                        </button>
                      ) : (
                        <button className="link" disabled={busy}
                                onClick={() => run(() =>
                                  admin.reactivateStation(station.code))}>
                          reactivate
                        </button>
                      )}
                    </div>
                  </td>
                </tr>
                {editing.mode === 'edit' && editing.code === station.code && (
                  <tr>
                    <td colSpan={7}>
                      {settings.loading && <p className="muted">Loading…</p>}
                      {settings.data && (
                        <StationForm
                          key={settings.data.code}
                          station={settings.data}
                          busy={busy}
                          onCancel={() => setEditing({ mode: 'none' })}
                          onSubmit={(body) => run(() =>
                            admin.updateStation(station.code, body))} />
                      )}
                    </td>
                  </tr>
                )}
              </Fragment>
            ))}
          </tbody>
        </table>
      )}
    </>
  )
}

/** Thousands of parameters once every gateway column has its own: searched,
 *  and drawn a hundred at a time. */
function ParametersTable({ parameters, loading, error, busy, defining, setDefining,
                           editing, setEditing, onCreate, onUpdate }: {
  parameters: AdminParameter[] | undefined
  loading: boolean
  error?: string
  busy: boolean
  defining: boolean
  setDefining: (v: boolean) => void
  editing: string | undefined
  setEditing: (code: string | undefined) => void
  onCreate: (body: ParameterWrite) => void
  onUpdate: (code: string, body: ParameterWrite) => void
}) {
  const [query, setQuery] = useState('')
  const all = parameters ?? []
  const matches = filterParameters(all, query)
  const { visible, more } = useShowMore(matches, 100, query)
  return (
    <>
      <div className="section-actions">
        {all.length > 0 && (
          <ListSearch value={query} onChange={setQuery}
                      placeholder="Search by code, name, unit, axis group or kind"
                      shown={matches.length} total={all.length}
                      noun={['parameter', 'parameters']} />
        )}
        {!defining && (
          <button className="button" onClick={() => setDefining(true)}>
            Define a parameter
          </button>
        )}
      </div>

      {defining && (
        <ParameterForm busy={busy}
                       onCancel={() => setDefining(false)}
                       onSubmit={(body) => {
                         onCreate(body)
                         setDefining(false)
                       }} />
      )}

      {loading && <p className="muted">Loading…</p>}
      {error && <p className="error">{error}</p>}
      {query && !matches.length && <p className="muted">No parameter matches “{query.trim()}”.</p>}

      {visible.length > 0 && (
        <table className="grid">
          <thead>
            <tr>
              <th>Parameter</th><th>Unit</th><th>Axis group</th>
              <th>Kind</th><th>Range</th><th>Channels</th><th></th>
            </tr>
          </thead>
          <tbody>
            {visible.map((p) => (
              <Fragment key={p.code}>
                <tr>
                  <td>
                    <strong>{p.display_name}</strong><br />
                    <code className="muted">{p.code}</code>
                  </td>
                  <td>{p.unit}</td>
                  <td><code>{p.axis_group}</code></td>
                  <td>{p.kind}</td>
                  <td className="muted">
                    {p.min_valid == null && p.max_valid == null
                      ? 'unbounded'
                      : `${p.min_valid ?? '−∞'} … ${p.max_valid ?? '∞'}`}
                  </td>
                  <td>
                    {p.mapped_channels}
                    {/* Nothing measures it, so it can still be changed or
                        removed freely. */}
                    {p.mapped_channels === 0 && (
                      <span className="pill neutral">unused</span>
                    )}
                  </td>
                  <td>
                    <div className="row-actions">
                      <button className="link" onClick={() =>
                        setEditing(editing === p.code ? undefined : p.code)}>
                        {editing === p.code ? 'close' : 'edit'}
                      </button>
                    </div>
                  </td>
                </tr>
                {editing === p.code && (
                  <tr>
                    <td colSpan={7}>
                      <ParameterForm parameter={p} busy={busy}
                                     onCancel={() => setEditing(undefined)}
                                     onSubmit={(body) => {
                                       onUpdate(p.code, body)
                                       setEditing(undefined)
                                     }} />
                    </td>
                  </tr>
                )}
              </Fragment>
            ))}
          </tbody>
        </table>
      )}
      {more}
    </>
  )
}

function AuditTable({ entries }: { entries: AuditEntry[] | undefined }) {
  const [query, setQuery] = useState('')
  const all = entries ?? []
  const matches = filterAudit(all, query)
  const { visible, more } = useShowMore(matches, 50, query)
  if (entries && !entries.length) return <p className="muted">Nothing yet.</p>
  return (
    <>
      <ListSearch value={query} onChange={setQuery}
                  placeholder="Search by who, what or detail"
                  shown={matches.length} total={all.length} noun={['change', 'changes']} />
      {query && !matches.length && <p className="muted">No change matches “{query.trim()}”.</p>}
      {visible.length > 0 && (
        <table className="grid">
          <tbody>
            {visible.map((entry) => (
              <tr key={entry.id}>
                <td className="muted nowrap">{formatDateTime(entry.at)}</td>
                <td>{entry.actor}</td>
                <td><code>{entry.action}</code></td>
                <td className="detail">{auditText(entry.detail)}</td>
              </tr>
            ))}
          </tbody>
        </table>
      )}
      {more}
    </>
  )
}
